import React from "react"
import {createPlan, updatePlanStartDate, setPlanCost, addPlanMeal, setPlanMealThickness, movePlanMeal, removePlanMeal, finishPlan, getPlanHistory, setHistoryPlanCost} from "../service"
import {planDays, dayLabel, formatCost} from "../plan"

// The dinner plan (ROADMAP "Plan"): an ordered sequence of in-scope Dinner
// meals over the next days, starting from a start date (the shop day). Each
// meal is 1 or 2 days thick (a 2-day meal is cooked once and eaten as
// leftovers the next day). Day math and the "good until" rule live in the
// pure helpers in plan.js (unit-tested there).
//
// Finish plan = the end-of-week close: the active plan retires to history
// and every regular meal is de-scoped (the server does both; the client
// awaits the finish, then re-syncs everything from the server).
// History (finished / superseded plans) renders under the plan with
// reduced saturation, progressively - an infinite scroller that loads
// older plans as the sentinel at the bottom comes into view.
//
// The server is the source of truth: every control updates local state
// optimistically and fires the matching request (same pattern as Meal.js).

// how many finished plans to add per batch of the history scroller
const HISTORY_BATCH = 8

function today(){
  const d = new Date()
  const month = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${d.getFullYear()}-${month}-${day}`
}

export default function Plan({plan, mealsFromServer, updatePlan, refreshFromServer}){

  const [confirming, setConfirming] = React.useState(false)
  const [history, setHistory] = React.useState([])
  const [historyCount, setHistoryCount] = React.useState(HISTORY_BATCH)
  const sentinelRef = React.useRef(null)
  // the history entry whose shop cost is being edited: {id, value} or null
  const [editingCost, setEditingCost] = React.useState(null)
  // set when Escape closes the editor - the blur that follows must not commit
  const costCancelled = React.useRef(false)

  function loadHistory(){
    getPlanHistory().then((data) => {
      if(data && Array.isArray(data.plans)){
        setHistory(data.plans)
      }
    })
  }

  // history loads on mount (and again after a plan is finished - the
  // just-finished plan has moved into it)
  React.useEffect(() => {
    loadHistory()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // the infinite scroller: when the sentinel at the bottom of the rendered
  // history comes into view, the next batch of older plans is added.
  // (jsdom has no IntersectionObserver - there the first batch just stays.)
  React.useEffect(() => {
    const sentinel = sentinelRef.current
    if(!sentinel || typeof IntersectionObserver === "undefined"){
      return
    }
    const observer = new IntersectionObserver((entries) => {
      if(entries.some((entry) => entry.isIntersecting)){
        setHistoryCount((count) => count + HISTORY_BATCH)
      }
    })
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [historyCount, history])

  function startPlan(){
    // no plan yet: start one from today, empty - meals are added below.
    createPlan(today()).then((data) => {
      if(data && data.id){
        updatePlan(data)
      }
    })
  }

  function handleStartDate(event){
    const startDate = event.target.value
    if(!startDate) return
    updatePlan((previousPlan) => ({...previousPlan, startDate: startDate}))
    updatePlanStartDate(startDate)
  }

  // the week's shop cost (the supermarket total for the plan's week).
  // an empty field clears it; half-typed garbage is ignored so the field
  // keeps what the server has until the value is usable.
  function handleCost(event){
    const raw = event.target.value
    const cost = raw === "" ? null : Number(raw)
    if(raw !== "" && (isNaN(cost) || cost < 0)) return
    updatePlan((previousPlan) => ({...previousPlan, cost: cost}))
    if(cost !== plan.cost){
      setPlanCost(cost)
    }
  }

  function startEditingCost(past){
    costCancelled.current = false
    const value = (past.cost === null || past.cost === undefined) ? "" : String(past.cost)
    setEditingCost({id: past.id, value: value})
  }

  function cancelEditingCost(){
    costCancelled.current = true
    setEditingCost(null)
  }

  function commitEditingCost(){
    if(costCancelled.current){
      costCancelled.current = false
      return
    }
    const editing = editingCost
    if(!editing) return
    setEditingCost(null)
    const past = (history || []).find((p) => p.id === editing.id)
    const current = past ? past.cost : null
    const raw = String(editing.value).trim()
    if(raw === ""){
      // an empty cost clears the logged one
      if(current !== null && current !== undefined){
        setHistory((plans) => plans.map((p) => p.id === editing.id ? {...p, cost: null} : p))
        setHistoryPlanCost(editing.id, null)
      }
      return
    }
    const cost = Number(raw)
    if(isNaN(cost) || cost < 0 || cost === current) return  // invalid or unchanged - reverts
    setHistory((plans) => plans.map((p) => p.id === editing.id ? {...p, cost} : p))
    setHistoryPlanCost(editing.id, cost)
  }

  function handleThickness(index){
    const meal = plan.meals[index]
    const thickness = meal.thickness === 1 ? 2 : 1
    updatePlan((previousPlan) => ({
      ...previousPlan,
      meals: previousPlan.meals.map((m,i) => i === index ? {...m, thickness: thickness} : m)
    }))
    setPlanMealThickness(meal.id, thickness)
  }

  function handleMove(index, delta){
    const target = index + delta
    if(target < 0 || target >= plan.meals.length) return
    const mealId = plan.meals[index].id
    updatePlan((previousPlan) => {
      const meals = [...previousPlan.meals]
      const [moved] = meals.splice(index, 1)
      meals.splice(target, 0, moved)
      //planDays sorts by position, so renumber to match the new order -
      //stale position values would silently undo the rendered move.
      return {...previousPlan, meals: meals.map((m, i) => ({...m, position: i}))}
    })
    movePlanMeal(mealId, target)
  }

  function handleRemove(index){
    const mealId = plan.meals[index].id
    updatePlan((previousPlan) => ({
      ...previousPlan,
      meals: previousPlan.meals.filter((m,i) => i !== index)
    }))
    removePlanMeal(mealId)
  }

  function handleAdd(mealId){
    const meal = (mealsFromServer || []).find((m) => m.id === mealId)
    updatePlan((previousPlan) => ({
      ...previousPlan,
      meals: [...previousPlan.meals, {
        id: mealId,
        label: meal ? meal.label : "new",
        position: previousPlan.meals.length,
        thickness: 1
      }]
    }))
    addPlanMeal(mealId)
  }

  async function handleFinish(){
    // await the close on the server BEFORE re-syncing, so the refresh
    // can't slip in front of it and show the pre-finish state.
    await finishPlan()
    setConfirming(false)
    if(refreshFromServer){
      await refreshFromServer()
    }
    loadHistory()
  }

  // the finished / superseded plans under the plan: reduced saturation,
  // one line per meal (a 2-day meal as a date range), and the week's shop
  // cost on the header (tap to log / edit it - the one editable part)
  function historySection(){
    if(history.length === 0){
      return null
    }
    const visible = history.slice(0, historyCount)
    return(
      <div className="plan--history">
        <h4>History</h4>
        {visible.map((past) => (
          <div className="plan--historyPlan" key={past.id}>
            <div className="plan--historyHeader">
              <span>
                {dayLabel(past.startDate)} · {past.meals.length} dinner{past.meals.length === 1 ? "" : "s"}
              </span>
              {editingCost && editingCost.id === past.id ? (
                <input
                  className="plan--historyCostInput"
                  type="number"
                  min="0"
                  step="0.01"
                  autoFocus
                  value={editingCost.value}
                  onChange={(event) => setEditingCost({...editingCost, value: event.target.value})}
                  onBlur={() => commitEditingCost()}
                  onKeyDown={(event) => {
                    if(event.key === "Enter") commitEditingCost()
                    if(event.key === "Escape") cancelEditingCost()
                  }}
                />
              ) : (
                <button className="plan--historyCost" onClick={() => startEditingCost(past)}>
                  {formatCost(past.cost) === null ? "add cost" : formatCost(past.cost)}
                </button>
              )}
            </div>
            {planDays(past).map((day) => (
              <div className="plan--row plan--historyRow" key={day.id}>
                <div className="plan--day">
                  {day.thickness === 2 ? `${day.firstLabel} – ${day.lastLabel}` : day.firstLabel}
                </div>
                <div className="plan--label">{day.label}</div>
              </div>
            ))}
          </div>
        ))}
        {history.length > historyCount && (
          <div className="plan--historySentinel" ref={sentinelRef}></div>
        )}
      </div>
    )
  }

  if(!plan){
    return(
      <div className="plan">
        <h4>No plan yet</h4>
        <p className="plan--empty">
          Start a plan: line up this week's dinners, starting from the shop
          day. Each meal takes one or two days (a two-day meal is cooked once
          and eaten as leftovers the next day).
        </p>
        <button className="plan--button plan--button--start" onClick={startPlan}>Start plan</button>
        {historySection()}
      </div>
    )
  }

  const days = planDays(plan)
  const plannedIds = new Set(plan.meals.map((m) => m.id))
  const available = (mealsFromServer || []).filter((meal) =>
    meal.id !== "0" && meal.inScope && meal.mealType === "Dinner" && !plannedIds.has(meal.id))

  return(
    <div className="plan">
      <div className="plan--date">
        <h4>Start</h4>
        <input type="date" value={plan.startDate} onChange={handleStartDate}/>
      </div>

      <div className="plan--date plan--date--cost">
        <h4>Shop cost</h4>
        <span className="plan--costPound">£</span>
        <input
          type="number"
          min="0"
          step="0.01"
          value={(plan.cost === null || plan.cost === undefined) ? "" : plan.cost}
          onChange={handleCost}/>
      </div>

      <h4>Order</h4>
      {days.map((day) => {
        const index = plan.meals.findIndex((m) => m.id === day.id)
        const double = day.thickness === 2
        return(
          <div className={double ? "plan--row plan--row--double" : "plan--row"} key={day.id}>
            {/*a 2-day meal: one date per line, double-height row*/}
            <div className={double ? "plan--day plan--day--double" : "plan--day"}>
              {double
                ? <><div>{day.firstLabel}</div><div>{day.lastLabel}</div></>
                : day.firstLabel}
            </div>
            <div className="plan--label">{day.label}</div>
            <div className="plan--controls">
              <button className="plan--button" title="days thick" onClick={() => handleThickness(index)}>{day.thickness}</button>
              <button className="plan--button" title="move up" onClick={() => handleMove(index, -1)}>^</button>
              <button className="plan--button" title="move down" onClick={() => handleMove(index, 1)}>v</button>
              <button className="plan--button plan--button--remove" onClick={() => handleRemove(index)}>X</button>
            </div>
          </div>
        )
      })}

      {available.length > 0 && (
        <div className="plan--available">
          <h4>Add</h4>
          {available.map((meal) => (
            <div className="plan--row" key={meal.id}>
              <div className="plan--label">{meal.label}</div>
              <div className="plan--controls">
                <button className="plan--button" onClick={() => handleAdd(meal.id)}>+</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {confirming ? (
        <div className="plan--confirm">
          <p className="plan--confirmText">
            Finish this plan? All meals go out of scope and the plan moves to
            the history below.
          </p>
          <div className="plan--confirmButtons">
            <button className="plan--confirmButton" onClick={() => setConfirming(false)}>Cancel</button>
            <button className="plan--confirmButton plan--confirmButton--finish" onClick={handleFinish}>Finish</button>
          </div>
        </div>
      ) : (
        <button className="plan--button plan--button--start plan--button--finish" onClick={() => setConfirming(true)}>Finish plan</button>
      )}

      {historySection()}
    </div>
  )
}